import {hpcAttrs} from '@github-ui/hpc'
import {startTransition, useCallback, useDeferredValue, useMemo, useState, type FormEvent, type Ref} from 'react'
import {useAttachments} from '@github-ui/attachments'
import {PaperAirplaneIcon, SquareFillIcon} from '@primer/octicons-react'
import {useDynamicTextareaHeight} from '@github-ui/use-dynamic-textarea-height'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {usePlaygroundManager} from '../../../contexts/PlaygroundManagerContext'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import type {ModelState} from '../../../types'
import {
  reachedMaxAttachments,
  supportImageWithoutText,
  useImageAttachmentSupported,
} from '../../../utils/image-validation'
import {mergeRefs} from '../../../utils/merge-refs'
import {textFromMessageContent} from '../../../utils/message-content-helper'
import {AttachmentButton} from './attachments/AttachmentButton'
import {AttachmentDropzone} from './attachments/AttachmentDropzone'
import {AttachmentPreviewOutlet} from './attachments/AttachmentPreviewOutlet'
import {ImprovePrompt} from './ImprovePrompt'

import styles from './PlaygroundChatInput.module.css'
import {IconButton} from '@primer/react'

interface PlaygroundChatInputProps {
  model: ModelState
  position: number
  stopStreamingMessages: () => void
  sendMessage: (message: string, attachments: string[]) => void
  ref?: Ref<HTMLTextAreaElement>
}

export function PlaygroundChatInput({
  model,
  position,
  stopStreamingMessages,
  sendMessage,
  ref,
}: PlaygroundChatInputProps) {
  const manager = usePlaygroundManager()
  const state = usePlaygroundState()

  const {chatInput, isLoading, chatClosed} = model
  const isSendingMessage = state.models.some(m => m.isLoading)

  const supportsAttachments = useImageAttachmentSupported(model.catalogData)
  const attachmentDisabled = reachedMaxAttachments(model.messages, model.modelInputSchema)
  const canAddAnotherImage = supportsAttachments && !attachmentDisabled

  const [chatAttachments, chatAttachmentsApi] = useAttachments()

  const [isAhead, setIsAhead] = useState(false)
  const [text, setText] = useState('')
  const deferredTextValue = useDeferredValue(text)
  const deferredChatInput = useDeferredValue(chatInput)
  const textValueFromManager = useMemo(() => textFromMessageContent(deferredChatInput), [deferredChatInput])

  const supportsImageOnly = supportImageWithoutText(model.catalogData.name)
  const hasImages = chatAttachments.attachments.length > 0

  const hasInput = text.trim().length > 0
  const canSubmit = hasInput || (hasImages && supportsImageOnly)

  const setTextOnManager = useTrackingCallback((newText: string) => {
    // If we're not syncing inputs, just set our own value
    if (!state.syncInputs) {
      manager.setChatInput(position, newText)
      return
    }

    // else, set the `newText` on all models
    for (let i = 0; i < state.models.length; manager.setChatInput(i++, newText));
  })

  /*
  Text updates can come from two places,
  1. Local updates (keystrokes in this input)
  2. Updates from the manager (compare mode or quick actions)

  Note; Local updates will always say "I am ahead" (a user can only type into one input at a time).

  While we're ahead we ignore the manager's value, so its (asynchronous) echo of our own
  keystrokes can't clobber freshly typed text. Once the manager has caught up to our text,
  we're back in sync and no longer ahead.
  */
  // Once the manager has caught up to our local text, we're no longer ahead
  if (isAhead && textValueFromManager === text) {
    setIsAhead(false)
  }

  // If we're not ahead and the manager has a newer value (quick action, or another synced
  // input), adopt it. Setting state during render restarts this render with the new value
  // instead of briefly flashing the stale one through an effect.
  if (!isAhead && textValueFromManager !== text) {
    setText(textValueFromManager)
  }

  /*
  Commit keystrokes to local state immediately (urgent) so the input stays responsive, and
  propagate to the manager inside a transition so syncing other inputs stays non-urgent.
  Marking ourselves "ahead" prevents the manager's echo from overwriting the local text.
  */
  const updateLocalText = useCallback(
    (newText: string) => {
      setText(newText)
      setIsAhead(true)
      startTransition(() => {
        setTextOnManager(newText)
      })
    },
    [setTextOnManager],
  )

  // We want the keystrokes to commit as fast as we can, to avoid a janky UI
  const handleInput = (event: FormEvent<HTMLTextAreaElement>) => updateLocalText(event.currentTarget.value)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Escape') {
      e.preventDefault()
      stopStreamingMessages()
    }

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      /*
      Note; Do not await here. A browser needs to know as soon as possible if it must
      prevent the bubble. Wrapping this callback in an `async`, will cause the `e.preventDefault()`
      to fire after a microtask tick. Which may cause them to "miss-fire" in some scenarios.
      `void`, lets us "fire-and-forget". Keeping both the linter happy, and the browser.
      */
      void handleSubmit(e)
    }
  }

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault()

    if (isLoading) return
    if (!canSubmit) return

    const attachments = canAddAnotherImage
      ? await Promise.all(chatAttachments.attachments.map(attachment => attachment.url()))
      : []

    sendMessage(text, attachments)
    updateLocalText('')
    chatAttachmentsApi.reset()
  }

  const {ref: heightRef, ...heightStyles} = useDynamicTextareaHeight({
    maxHeightLines: 10,
    minHeightLines: 1,
    value: deferredTextValue,
  })
  const textareaRef = useMemo(() => mergeRefs([ref, heightRef]), [ref, heightRef])

  return (
    <AttachmentDropzone enabled={supportsAttachments && !attachmentDisabled}>
      <div className={styles.PlaygroundChatInputContainer}>
        {supportsAttachments && !attachmentDisabled ? <AttachmentPreviewOutlet /> : null}

        <form className={styles.PlaygroundChatInput} onSubmit={handleSubmit}>
          <textarea
            value={text}
            ref={textareaRef}
            className={styles.textarea}
            rows={1}
            {...hpcAttrs()}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            aria-multiline="true"
            aria-label="Prompt"
            placeholder="Type your prompt…"
            disabled={chatClosed}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            style={heightStyles}
          />

          <div className={styles.formButtons}>
            {isLoading ? (
              <IconButton
                variant="invisible"
                size="small"
                onClick={stopStreamingMessages}
                icon={SquareFillIcon}
                aria-label="Stop"
                tooltipDirection="w"
              />
            ) : (
              <>
                <ImprovePrompt prompt={text} handleUpdatePrompt={updateLocalText} type="user" />
                {supportsAttachments ? <AttachmentButton disabled={attachmentDisabled} /> : null}
                <IconButton
                  variant="invisible"
                  size="small"
                  onClick={handleSubmit}
                  icon={PaperAirplaneIcon}
                  disabled={!canSubmit || isSendingMessage || chatClosed}
                  aria-label="Send now"
                  tooltipDirection="w"
                />
              </>
            )}
          </div>
        </form>
      </div>
    </AttachmentDropzone>
  )
}

// `: any` here gives us the ability to type narrow, where `: unknown` would not
// eg: const cb = useTrackingCallback((foo: string) => boolean)
//          ^? (foo: string) => boolean
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useTrackingCallback<T extends (...args: any[]) => any>(callback: T): T {
  const ref = useTrackingRef(callback)

  return useCallback((...args: Parameters<T>) => ref.current(...args), [ref]) as T
}
